import React, { useEffect, useRef, useState } from 'react';
import CardStoreList from './CardStoreList';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import ButtonPrimary from '../Form/ButtonPrimary';
import { TbCurrentLocation } from 'react-icons/tb';
import StoreService, { StoreListProps } from '../../Services/StoreService';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { ImSpinner8 } from 'react-icons/im';
import { useLoginContext } from '../../Contexts/LoginContext';
import { useStoreContext } from '../../Contexts/StoreContext';
import { orderTypeAsSlug } from '../../Utils/Helpers';
import Confirm from './Confirm';
import Modal from './Modal';
import { useCartHook } from '../../Hooks/CartHook';
import { useCartContext } from '../../Contexts/CartContext';
import { ORDER_TYPE_MENU, ORDER_TYPE_STORE_COUNTER } from '../../Utils/Constants/Service';
import HeaderLogo from './HeaderLogo';
import Button from '../Form/Button';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useTranslation } from 'react-i18next';
import { ALL_STATES } from '../../Utils/Constants/Global';
import StoreListMapGoogle from './StoreListMapGoogle';
import BoxContentSearchStoreByLocation from './BoxContentSearchStoreByLocation';

interface StoreListPageProps {
    isInDrawer?: boolean;
}

const StoreList = ({ isInDrawer=false }: StoreListPageProps) => {

    const { t } = useTranslation();
    const [ stores, setStores ] = useState<any>([]);
    const [ storesFiltered, setStoresFiltered ] = useState<any>([]);
    const navigate = useNavigate();
    const { orderType, getOrderTypeIdByName, visibleOrderType } = useOrderTypeContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconNoSearchMap, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconError, company, states, setStates } = useLayoutContext();
    const { setGeolocationIsBlocked } = useLoginContext();
    const { currentStore, setCurrentStore } = useStoreContext();
    const [location, setLocation] = useState({
        currentState: null as string | null,
        currentCity: null as string | null,
        currentNeighborhood: null as string | null,
        cities: [] as string[],
        neighborhoods: [] as string[],
        loadingCities: false,
        loadingNeighborhoods: false,
        loadingStates: false,
        loadingStores: false,
    });

    const updateLocation = (updates: Partial<typeof location>) => {
        setLocation(prev => ({ ...prev, ...updates }));
    };

    const {
        currentState, currentCity, currentNeighborhood,
        cities, neighborhoods,
        loadingCities, loadingNeighborhoods, loadingStates, loadingStores
    } = location;
    const [ showConfirmChangeStore, setShowConfirmChangeStore ] = useState(false);
    const [ storeToSelect, setStoreToSelect ] = useState<any>(null);
    const { currentCart, cartDisplay } = useCartContext();
    const [ currentOrderType, setCurrentOrderType ] = useState<any>(null);
    const { removeAll } = useCartHook();
    const { loadCompanyInfo } = useStoreHook();
    const [ listStoresWithLocation, setListStoresWithLocation ] = useState(false);
    const isFirstRender = useRef(true);

    const [ selected, setSelected ] = useState<any>(null);

    const resetCartItems = (store: any) => {
        return currentStore && currentCart?.items?.length > 0 && currentStore.id !== store.id;
    }

    const handleClickStore = (store: any) => {
        if (!orderType) {
            navigate(`/${ORDER_TYPE_MENU}/${store.slug}`);
            return;
        }

        if (resetCartItems(store)) {
            setShowConfirmChangeStore(true);
            setStoreToSelect(store);
        } else {
            changeStore(store);
        }
    }

    const changeStore = (store: any) => {
        if (store.slug) {
            setCurrentStore(store);
            setStoreToSelect(null);
            if (resetCartItems(store)) {
                const payload = {
                    order_type: Number(getOrderTypeIdByName(orderType))
                }
                removeAll(currentCart.cart_id, payload);
            }
            navigateToStore(store);
        } else {
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle(t('layout.store_without_slug'));
            setMainLoadingMessage('');
            setMainLoadingCloseButton(true);
        }
    }

    const navigateToStore = (store: any) => {
        navigate(`/${orderTypeAsSlug(orderType)}/${store.slug}`);
    }

    const handleGetLocation = () => {
        updateLocation({ currentState: '', currentCity: '', currentNeighborhood: '' });
        setCurrentOrderType(null);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              loadStoresWithLocation({ latitude, longitude });
              setGeolocationIsBlocked(false);
            },
            (error) => {
                setGeolocationIsBlocked(true)
                setTimeout(() => {
                    setMainLoading(true);
                    setMainLoadingIcon(false);
                    setMainLoadingIconNoSearchMap(true);
                    setMainLoadingTitle(t('layout.geolocation_permission.denied.title'));
                    setMainLoadingMessage(t('layout.geolocation_permission.denied.message'));
                    setMainLoadingCloseButton(true);
                }, 250);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        } else {
          console.error(t('layout.geolocation_permission.denied.not_supported_by_browser'));
        }
    };

    useEffect(() => {
            const filtered = stores.filter((store: any) => {
                //* Verifica se a loja possui o serviço ativo para o tipo atual
                const hasOrderType =
                    currentOrderType ?
                        Array.isArray(store.store_services) &&
                        store.store_services.some(
                            (service: any) => service.order_type === currentOrderType?.id && service.active
                        )
                    : true;
                return hasOrderType;
            });
        setStoresFiltered(filtered);
    }, [stores, currentOrderType]);


    useEffect(() => {

        if (!isInDrawer) {
            document.title = t('layout.store_list.title');
            loadCompanyInfo();
        }

        //* Redirecionando para home da loja atual (auto-atendimento não possui tela de lojas)
        if (orderType === ORDER_TYPE_STORE_COUNTER && currentStore && cartDisplay) {
            navigate(`/${orderTypeAsSlug(orderType)}/${currentStore.slug}`);
        }

        const loadStates = async () => {
            updateLocation({ loadingStates: true, cities: [], neighborhoods: [], currentCity: null, currentState: null });
            try {
                const storeService = new StoreService(orderType);
                let statesData = await storeService.states();

                if (!statesData.includes('')) {
                    statesData = ['', ...statesData];
                }

                setStates(statesData);
            } catch (error) {
                let statesData = ALL_STATES;

                if (!statesData.includes('')) {
                    statesData = ['', ...statesData];
                }

                setStates(statesData);
            } finally {
                updateLocation({ loadingStates: false });
                handleGetLocation();
            }
        }

        loadStates();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCities = async (stateSelection: string) => {
        if (!stateSelection) return;
        updateLocation({ loadingCities: true, currentCity: null, cities: [] });
        try {
            const citiesData = await new StoreService(orderType).cities(stateSelection);
            updateLocation({ cities: citiesData, loadingCities: false });
        } catch (error) {
            updateLocation({ loadingCities: false });
        }
    }

    const loadNeighborhoods = async (stateSelection: string, citySelection: string) => {
        if (!stateSelection || !citySelection) return;
        updateLocation({ loadingNeighborhoods: true, currentNeighborhood: null, neighborhoods: [] });
        try {
            const neighborhoodsData = await new StoreService(orderType).neighborhoods(stateSelection, citySelection);
            updateLocation({ neighborhoods: neighborhoodsData, loadingNeighborhoods: false });
        } catch (error) {
            updateLocation({ loadingNeighborhoods: false });
        }
    }

    const loadStoresWithLocation = (params: any) => {
        setListStoresWithLocation(true);
        loadStoresList(params);
    }

    const loadStoresList = async (params?: StoreListProps) => {

        if (isFirstRender.current) {
            isFirstRender.current = false;
        }

        setStores([]);
        updateLocation({ loadingStores: true });

        try {
            const storeService = new StoreService(orderType);
            const payload = {
                state: currentState?.toLowerCase(),
                city: currentCity?.toLowerCase(),
                neighborhood: currentNeighborhood?.toLowerCase(),
                latitude: params?.latitude,
                longitude: params?.longitude,
            }
            const stores = await storeService.stores(payload);

            setStores(stores);
            setStoresFiltered(stores);

        } catch (error) {
            setStores([]);
        } finally {
            updateLocation({ loadingStores: false });
        }
    }

    const handleCurrentOrderType = (service: any) => {
        setCurrentOrderType(service);
    }

    useEffect(() => {
        if (currentState) {
            loadCities(currentState);
        } else {
            updateLocation({ currentCity: '', currentNeighborhood: '', cities: [], neighborhoods: [] });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentState])

    useEffect(() => {
        if (currentState && currentCity) {
            loadNeighborhoods(currentState, currentCity);
        } else {
            updateLocation({ currentNeighborhood: '', neighborhoods: [] });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentState, currentCity])

    return (
        <>


            <div className={`flex flex-col h-full w-full ${isInDrawer ? 'gap-2 pb-4' : 'gap-4 lg:gap-1 lg:flex-row overflow-y-auto max-h-[calc(100dvh-8rem)]'}`}>

                {/* Form */}
                <section className={`flex flex-col gap-6 bg-gray-50/85 backdrop-blur-sm rounded-2xl ${isInDrawer ? 'p-6 shadow-lg shadow-gray-200/50' : 'p-6 lg:sticky top-0 z-10 '}`}>

                    {/* Header */}
                    <div className={`grid gap-1.5 ${isInDrawer ? 'text-center pb-2' : ''}`}>

                        {(company && isInDrawer) && (
                            <div className='flex justify-center text-center w-full'>
                                <HeaderLogo className='w-46 justify-center' classNameImage='min-h-46' />
                            </div>
                        )}

                        <h1 className={`font-bold leading-none text-shopfood-title ${isInDrawer ? 'text-xl mt-1' : 'text-xl md:text-2xl'}`}>{t('layout.store_list.title')}</h1>
                        <span className='text-base leading-none'>{t('layout.store_list.subtitle')}</span>
                    </div>

                    <div className='grid gap-3 grid-cols-2'>

                        {/* STATES */}
                        <div className='col-span-2'>
                            <label htmlFor="state" className="font-semibold text-shopfood-title">{t('layout.store_list.form.state')}</label>
                            <select
                                name="state"
                                disabled={loadingStates || loadingStores || states.length === 0}
                                value={currentState || ''}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    updateLocation({ currentState: e.target.value });
                                }}
                                className="disabled:bg-gray-200 disabled:text-gray-400 relative block rounded border-0 w-full p-2.5 ring-1 ring-gray-300 text-shopfood-title placeholder:font-normal placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                >
                                    {states.map((uf: string) => {
                                        if (!uf) {
                                            return <option key={uf} value="">{t('layout.select_label')}</option>
                                        }
                                        return <option key={uf} value={uf}>{uf}</option>
                                    })}
                            </select>
                        </div>

                        {currentState && (
                            <>
                                {/* CITIES */}
                                <div>
                                    <label htmlFor="city" className="font-semibold text-shopfood-title">{t('layout.store_list.form.city')}</label>
                                    <select
                                        name="city"
                                        disabled={loadingCities || loadingStores || cities.length === 0}
                                        value={currentCity || ''}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            updateLocation({ currentCity: e.target.value });
                                        }}
                                        className="disabled:bg-gray-200 disabled:text-gray-400 relative block rounded border-0 w-full p-2.5 ring-1 ring-gray-300 text-shopfood-title placeholder:font-normal placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        >
                                            <option value="">{loadingCities ? t('layout.loading_main.default_message') : t('layout.store_list.filter_all_cities')}</option>
                                            {cities.map((uf: string) => (
                                                <option key={uf} value={uf}>{uf}</option>
                                            ))}
                                    </select>
                                </div>

                                {/* Neighborhood */}
                                <div>
                                    <label htmlFor="neighborhood" className="font-semibold text-shopfood-title">{t('layout.store_list.form.neighborhood')}</label>
                                    <select
                                        name="neighborhood"
                                        disabled={!currentCity || loadingNeighborhoods || loadingStores || neighborhoods.length === 0}
                                        value={currentNeighborhood || ''}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            updateLocation({ currentNeighborhood: e.target.value });
                                        }}
                                        className="disabled:bg-gray-200 disabled:text-gray-400 relative block rounded border-0 w-full p-2.5 ring-1 ring-gray-300 text-shopfood-title placeholder:font-normal placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                        >
                                            <option value="">{loadingNeighborhoods ? t('layout.loading_main.default_message') : t('layout.store_list.filter_all')}</option>
                                            {neighborhoods.map((uf: string) => (
                                                <option key={uf} value={uf}>{uf}</option>
                                            ))}
                                    </select>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-wrap w-full gap-2">

                        <ButtonPrimary disabled={loadingStores || !currentState} onClick={() => {loadStoresList(); setListStoresWithLocation(false);}} className="w-full items-center justify-center gap-1 text-nowrap flex-1">
                            {loadingStores ? <ImSpinner8 className="animate-spin" /> : t('layout.store_list.button_search_label')}
                        </ButtonPrimary>
                        <ButtonPrimary outline onClick={() => handleGetLocation()} className="w-full items-center justify-center gap-1 text-nowrap flex-1">
                            <span><TbCurrentLocation size={20} /></span>
                            <span>{t('layout.geolocation_permission.button_use_geolocation_label')}</span>
                        </ButtonPrimary>

                    </div>


                    {company?.google_maps && !isInDrawer && stores && storesFiltered.length > 0 && (
                        <div className={`hidden lg:grid h-full w-full overflow-hidden relative mt-2 rounded-xl ${loadingStores ? 'bg-gray-100/75' : 'ring-4 ring-white shadow-xl shadow-gray-200'}`}>
                            <StoreListMapGoogle
                                loading={loadingStores}
                                stores={storesFiltered}
                                onClickStore={(store: any) => setSelected(store)}
                                onClose={() => setSelected(null)}
                                selected={selected}
                            />
                        </div>
                    )}
                </section>










                <section className={`w-full flex flex-col gap-2 px-4 py-0.5 ${isInDrawer || loadingStores ? 'h-full' : 'min-h-max'}`}>

                    {loadingStores && (
                        <div className='flex gap-1 justify-center items-center text-gray-500 h-full'>
                            <ImSpinner8 className="animate-spin" />
                            {t('layout.store_list.loading')}
                        </div>
                    )}

                    {/* Services Filter */}
                    {!loadingStores && visibleOrderType.length > 1 && storesFiltered.length > 0 &&
                        <>
                            <section className={`flex items-center w-full sticky top-0 z-10 backdrop-blur-sm py-2 ${isInDrawer ? 'bg-gray-100/75' : 'bg-white/75'}`}>
                                <div className="flex flex-wrap w-auto gap-1">

                                    {!currentOrderType
                                        ?
                                            <ButtonPrimary
                                                outline
                                                onClick={() => setCurrentOrderType(null)}
                                                className="flex-1 ring-inset max-h-fit text-sm"
                                                >
                                                {t('layout.store_list.filter_all')}
                                            </ButtonPrimary>
                                        :
                                            <Button
                                                onClick={() => setCurrentOrderType(null)}
                                                className="flex-1 ring-inset max-h-fit text-sm"
                                                >
                                                {t('layout.store_list.filter_all')}
                                            </Button>
                                    }

                                    {visibleOrderType.map((service: any, index: number) => {
                                        return (
                                            service.name === currentOrderType?.name
                                                ?
                                                    <ButtonPrimary
                                                        outline
                                                        key={index}
                                                        onClick={() => handleCurrentOrderType(service)}
                                                        className="text-nowrap gap-1 flex-1 ring-inset max-h-fit text-sm"
                                                        >
                                                        <span>{service.label}</span>
                                                    </ButtonPrimary>
                                                :
                                                    <Button
                                                        key={index}
                                                        onClick={() => handleCurrentOrderType(service)}
                                                        className="text-nowrap gap-1 flex-1 ring-inset max-h-fit text-sm"
                                                        >
                                                        <span>{service.label}</span>
                                                    </Button>
                                        )
                                    })}

                                </div>
                            </section>

                            <BoxContentSearchStoreByLocation show={listStoresWithLocation} />
                        </>
                    }

                    {(!loadingStores && stores && storesFiltered.length > 0) && (
                        <section className={`grid grid-cols-1 content-start ${isInDrawer ? 'grid-cols-1' : 'pl-2 xl:grid-cols-2 2xl:grid-cols-3__'} gap-4 transition-width ease-in-out duration-200`}>
                            {storesFiltered.map((store: any) =>
                                <CardStoreList 
                                    key={`${store.id}`} 
                                    orderType={orderType} 
                                    enableCardClick={false} 
                                    showServices 
                                    onCardClick={() => handleClickStore(store)} 
                                    store={store} 
                                />
                            )}
                        </section>
                    )}

                </section>


            </div>

            {(currentStore && storeToSelect) &&
                <Modal open={showConfirmChangeStore} className="max-w-sm m-4" onClose={() => setShowConfirmChangeStore(false)}>
                    <Confirm
                        title={t('layout.store_list.confirm_change_title')}
                        message={t('layout.store_list.confirm_change_message', {currentStore: currentStore?.name, newStore: storeToSelect?.name})}
                        labelConfirm={t('layout.store_list.confirm_button_label')}
                        onConfirm={() => {
                            changeStore(storeToSelect);
                        }}
                        onCancel={() => {
                            setShowConfirmChangeStore(false);
                        }}
                    />
                </Modal>
            }
        </>
    );
}

export default StoreList;
